'use client'

import { useState, useContext } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { Label } from '@/components/ui/shadcn/label'
import { Separator } from '@/components/ui/shadcn/separator'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/shadcn/dialog'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { AlertCircle } from 'lucide-react'
import { fetcher } from '@/lib/api/fetch'
import { AuthContext } from '@/contexts/AuthContext'

export default function ConfirmAccount({ open, setOpen }) {
  const authContext = useContext(AuthContext)
  const [error, setError] = useState(null)

  const submit = async e => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const email = formData.get('email')

    // Check the email address address matches the user's email address
    if (email !== authContext.user.username) {
      setError('Email address does not match')
      return
    }

    // Call the API to remove the user
    const res = await fetcher(
      '/api/admin/user/' + authContext.user.id,
      { method: 'DELETE' },
      authContext,
    )

    // If the API call was successful, sign the user out
    if (res.ok) {
      authContext.signout(() => {
        window.location.href = '/'
      })
    } else {
      // If the API call failed, show the error
      const data = await res.json()
      setError(data.message)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v) setOpen(false)
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Remove my account</DialogTitle>
          <DialogDescription>
            Removing your account will also remove all of your data. This action
            cannot be undone. This includes access to all organisations you have
            been invited to.
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm mt-2 mb-2">
          To confirm the removal of your account type your full email address{' '}
          <b>{authContext.user.username}</b>
        </p>
        <Separator />
        {error && (
          <Alert variant="destructive" className="my-2">
            <AlertCircle className="size-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <form onSubmit={submit}>
          <div className="space-y-2 mb-4">
            <Label htmlFor="email">Email Address</Label>
            <Input id="email" name="email" type="email" required />
          </div>
          <Button type="submit" variant="destructive" className="w-full">
            Remove My Account
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full mt-2"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
